import { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Ticket, CheckCircle, Loader2, Home, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Confirmation() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // State from navigation (legacy flow)
  const { tickets: stateTickets, raffle: stateRaffle, amount: stateAmount } = location.state || {};
  
  const [paymentStatus, setPaymentStatus] = useState(sessionId ? 'checking' : 'confirmed');
  const [error, setError] = useState('');

  const pollPaymentStatus = useCallback(async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setPaymentStatus('confirmed');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('confirmed');
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
        setError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setPaymentStatus('confirmed');
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId, pollPaymentStatus]);

  // Loading state while checking payment
  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#EAB308] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
          <p className="text-gray-500">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/" className="bg-[#EAB308] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#CA8A04] transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
              
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-heading text-3xl font-black text-gray-900 mb-2" data-testid="confirmation-title">
            Payment Confirmed!
          </h1>
          <p className="text-gray-500">
            Your tickets are now active. Good luck!
          </p>
        </div>

        {/* Order Details (if available from state) */}
        {stateRaffle && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Order Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Free Draw</span>
                <span className="text-gray-900 font-medium">{stateRaffle.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Prize</span>
                <span className="text-[#EAB308] font-bold">£{stateRaffle.prize_amount?.toLocaleString()}</span>
              </div>
              {stateAmount && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount Paid</span>
                  <span className="text-gray-900 font-medium">£{stateAmount?.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tickets (if available from state) */}
        {stateTickets && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#EAB308]" />
              Your Tickets
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="confirmation-tickets">
              {stateTickets.map(t => t.ticket_number).sort((a, b) => a - b).map(num => (
                <span 
                  key={num}
                  className="bg-yellow-50 border border-[#EAB308]/30 text-[#EAB308] px-3 py-1 rounded-lg font-mono font-bold"
                >
                  #{num}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Status confirmed */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-green-800 font-bold">Payment Confirmed</p>
              <p className="text-green-600 text-sm">Your tickets are active and entered in the draw!</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link 
            to="/my-tickets"
            className="flex-1 bg-white border border-gray-200 text-gray-900 font-bold py-4 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            data-testid="view-tickets-button"
          >
            <Ticket className="w-5 h-5" />
            View My Tickets
          </Link>
          <Link 
            to="/"
            className="flex-1 bg-[#EAB308] text-white font-bold py-4 rounded-xl hover:bg-[#CA8A04] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#EAB308]/25"
            data-testid="home-button"
          >
            <Home className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}
